import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { TreePine, Camera, Award, Calendar, Users, Vote, Star, Medal, CheckCircle } from 'lucide-react';
import Footer from '../components/Footer';
import RulesHero from '../components/rules/RulesHero';
import EvaluationCriteria from '../components/rules/EvaluationCriteria';
import DisqualificationReasons from '../components/rules/DisqualificationReasons';
import TelegramCTA from '../components/rules/TelegramCTA';

const sections = [
  {
    title: "Requisitos de Participación",
    icon: Users,
    color: "bg-blue-50 text-blue-600",
    rules: [
      "Debes ser mayor de 18 años",
      "Solo se permite una participación por persona",
      "El árbol debe ser decorado por ti mismo",
      "Residir en España peninsular (no incluye Islas Canarias, Ceuta, Melilla y Baleares)"
    ]
  },
  {
    title: "Criterios de la Imagen",
    icon: Camera,
    color: "bg-purple-50 text-purple-600",
    rules: [
      "Resolución mínima: 1920x1080 píxeles",
      "La foto debe ser clara, bien iluminada y mostrar el árbol completo",
      "No se permiten fotos de internet o de años anteriores"
    ]
  },
  {
    title: "Fechas Importantes",
    icon: Calendar,
    color: "bg-red-50 text-red-600",
    rules: [
      "Inicio del concurso: 5 de diciembre",
      "Cierre de votaciones: 24 de diciembre por la tarde (antes de la cena de Nochebuena)",
      <>Anuncio de ganadores: 25 de diciembre por el canal oficial de Telegram <a href="https://t.me/ofertium_es" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-dark">@ofertium_es</a></>
    ]
  },
  {
    title: "Proceso de Votación",
    icon: Vote,
    color: "bg-green-50 text-green-600",
    rules: [
      "Cada usuario puede votar una vez cada 12 horas por árbol",
      "Los votos son verificados para prevenir duplicados",
      "El sistema de votación es transparente y auditable",
      "Los tres árboles más votados recibirán los premios en orden descendente"
    ]
  }
];

const evaluationCriteria = [
  {
    title: "Creatividad",
    icon: Star,
    description: "Originalidad y singularidad en la decoración",
    details: "Valoramos las ideas innovadoras y únicas en la decoración del árbol. Cuanto más original y creativo sea tu diseño, mejor puntuación obtendrás."
  },
  {
    title: "Iluminación",
    icon: TreePine,
    description: "Uso efectivo de luces y colores",
    details: "La iluminación debe realzar la decoración y crear un ambiente navideño. Considera el balance entre diferentes tipos de luces y su distribución."
  },
  {
    title: "Armonía",
    icon: Medal,
    description: "Balance y composición general del diseño",
    details: "Todos los elementos deben complementarse entre sí. Busca un equilibrio visual en la distribución de adornos y colores."
  },
  {
    title: "Presentación",
    icon: Camera,
    description: "Calidad y claridad de la fotografía",
    details: "La foto debe mostrar claramente tu árbol. Asegúrate de que la iluminación sea adecuada y que se vean todos los detalles."
  }
];

const disqualificationReasons = [
  {
    reason: "Incumplimiento de las reglas establecidas",
    details: "No seguir las normas básicas del concurso resultará en descalificación inmediata."
  },
  {
    reason: "Uso de contenido inapropiado o copias de otros participantes",
    details: "El plagio o contenido inadecuado no será tolerado bajo ninguna circunstancia."
  },
  {
    reason: "Intento de manipulación del sistema de votación",
    details: "Cualquier intento de alterar el sistema de votación resultará en descalificación."
  },
  {
    reason: "Información falsa o engañosa en la participación",
    details: "Proporcionar datos falsos o engañosos invalidará tu participación."
  }
];

export default function Rules() {
  const [openCriteria, setOpenCriteria] = useState<string | null>(null);

  return (
    <>
      <Helmet>
        <title>Reglas del Concurso - Concurso de Árboles de Navidad</title>
        <meta name="description" content="Conoce las reglas y bases de nuestro concurso de árboles de Navidad. Participa y gana increíbles premios decorando tu árbol." />
        <link rel="canonical" href="https://arbolnavidad.com/reglas" />
      </Helmet>
      
      <div className="min-h-screen pt-16">
        <RulesHero />

        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto space-y-12">
            {/* Main Rules */}
            {sections.map((section) => (
              <div key={section.title} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className={`px-6 py-4 ${section.color}`}>
                  <div className="flex items-center">
                    <section.icon className="h-6 w-6 mr-3" />
                    <h2 className="font-bold text-xl">{section.title}</h2>
                  </div>
                </div>
                <div className="p-6">
                  <ul className="space-y-4">
                    {section.rules.map((rule, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{rule}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}

            <EvaluationCriteria 
              criteria={evaluationCriteria}
              openCriteria={openCriteria}
              onCriteriaToggle={setOpenCriteria}
            />

            <DisqualificationReasons reasons={disqualificationReasons} />

            <TelegramCTA />
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}